<template>
  <div class="main">
    <div class="login">
      <div class="login-left">
        <img style="height: 100%" :src="registerLeftUrl" alt="loginLeft" />
      </div>
      <div class="login-right">
        <div class="login-right-title">
          <span class="title-text">用户注册</span>
          <div class="title-line"></div>
        </div>
        <el-form
          :model="form"
          :rules="rules"
          ref="formRef"
          label-width="0px"
          class="register-form"
        >
          <div class="form-item">
            <div class="login-right-label">用户名:</div>
            <el-form-item prop="username">
              <el-input
                v-model="form.username"
                placeholder="请输入您的用户名"
                prefix-icon="User"
              ></el-input>
            </el-form-item>
          </div>

          <div class="form-item">
            <div class="login-right-label">密码:</div>
            <el-form-item prop="password">
              <el-input
                type="password"
                v-model="form.password"
                placeholder="请输入您的密码"
                prefix-icon="Lock"
                show-password
              ></el-input>
            </el-form-item>
          </div>

          <div class="form-item">
            <div class="login-right-label">手机号:</div>
            <el-form-item prop="phone">
              <el-input
                v-model="form.phone"
                placeholder="请输入您的手机号"
                prefix-icon="Phone"
              ></el-input>
            </el-form-item>
          </div>

          <div class="form-item">
            <div class="login-right-label">单位名称:</div>
            <el-form-item prop="company">
              <el-input
                v-model="form.company"
                placeholder="请输入您的单位名称"
                prefix-icon="Office"
              ></el-input>
            </el-form-item>
          </div>

          <div class="form-item">
            <div class="login-right-label">真实姓名:</div>
            <el-form-item prop="name">
              <el-input
                v-model="form.name"
                placeholder="请输入您的真实姓名"
                prefix-icon="User"
              ></el-input>
            </el-form-item>
          </div>

          <el-form-item prop="agree" class="agree-item">
            <div class="agree-wrapper">
              <el-checkbox
                v-model="form.agree"
                label="我同意服务条款"
                size="large"
              />
              <div class="divider">|</div>
              <span class="login-link" @click="router.push('/login')"
                >去登录</span
              >
            </div>
          </el-form-item>

          <el-form-item>
            <el-button type="primary" class="submit-btn" @click="onSubmit"
              >注册</el-button
            >
          </el-form-item>
        </el-form>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { ElForm } from "element-plus";
import { useRouter } from "vue-router";
import { ElMessage } from "element-plus";
import { register } from "@/api/index";
import { getSystemConfig } from "@/utils/index";

const {
  images: { registerLeftUrl },
} = getSystemConfig();

const router = useRouter();
const form = ref({
  username: "",
  name: "",
  agree: false,
  password: "",
  company: "",
  phone: "",
});

const validateAgree = (
  rule,
  value: boolean,
  callback: (err?: string) => void
) => {
  if (!value) return callback("请勾选服务条款");
  return callback();
};

const rules = {
  username: [
    { required: true, message: "请输入用户名" },
    { min: 2, max: 20, message: "用户名长度在 2 到 20 个字符" },
  ],
  company: [{ required: true, message: "请输入单位名称" }],
  password: [
    { required: true, message: "请输入密码" },
    { min: 6, message: "密码长度不能少于6位" },
  ],
  agree: { validator: validateAgree },
};

const formRef = ref<typeof ElForm>();

const onSubmit = async () => {
  const valid = await formRef.value?.validate().catch(() => false);
  if (!valid) return;
  const { agree, ...rest } = form.value;
  const res = await register({ ...rest });
  if (res?.code === "success") {
    ElMessage.success("注册成功");
  }
};
</script>

<style lang="scss" scoped>
.main {
  height: 100vh;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f5f7fa 0%, #e4e8f0 100%);
}

.login {
  width: 720px;
  display: flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  padding: 40px;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.12);
  }

  &-left {
    margin-right: 40px;
    flex: 1;
    height: 100%;
    img {
      border-radius: 12px;
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
      transition: transform 0.3s ease;

      &:hover {
        transform: scale(1.02);
      }
    }
  }

  &-right {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 400px;

    &-title {
      position: relative;
      text-align: center;
      margin: 0 0 40px;

      .title-text {
        color: #1a1a1a;
        font-weight: 600;
        font-size: 32px;
        background: linear-gradient(45deg, #4f46e5, #7c3aed);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
      }

      .title-line {
        position: absolute;
        bottom: -10px;
        left: 50%;
        transform: translateX(-50%);
        width: 40px;
        height: 3px;
        background: linear-gradient(90deg, #4f46e5, #7c3aed);
        border-radius: 2px;
      }
    }

    &-label {
      color: #374151;
      font-size: 14px;
      margin-bottom: 8px;
      font-weight: 500;
    }
  }
}

.register-form {
  width: 100%;

  .form-item {
    margin-bottom: 20px;
  }

  :deep(.el-input) {
    .el-input__wrapper {
      box-shadow: 0 0 0 1px #e5e7eb inset;
      transition: all 0.3s ease;
      border-radius: 8px;

      &:hover {
        box-shadow: 0 0 0 1px #4f46e5 inset;
      }

      &.is-focus {
        box-shadow: 0 0 0 2px #4f46e5 inset;
        transform: translateY(-1px);
      }
    }
  }
}

.agree-wrapper {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin: 20px 0;

  .divider {
    color: #e5e7eb;
    margin: 0 16px;
  }

  .login-link {
    color: #4f46e5;
    cursor: pointer;
    font-size: 14px;
    transition: all 0.3s ease;

    &:hover {
      color: #7c3aed;
      text-decoration: underline;
    }
  }
}

.submit-btn {
  width: 100%;
  height: 44px;
  background: linear-gradient(45deg, #4f46e5, #7c3aed);
  border: none;
  font-size: 16px;
  font-weight: 500;
  margin-top: 20px;
  border-radius: 8px;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(79, 70, 229, 0.4);
  }

  &:active {
    transform: translateY(0);
  }
}
</style>
